import { DefaultOptionType } from 'antd/es/select';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import AppTooltip from '@/components/app-tooltip/AppTooltip';
import CountryFlag from '@/components/common/country-flag';
import FormItem from '@/components/common/form-item';
import { TagRenderComponent } from '@/components/form-select/FormSelect';
import { ISelectCustomProps, SelectCustom } from '@/components/form-select/SelectCustom';

import useFetch from '@/hooks/useFetch';
import { mappingOptionsCountry } from '@/hooks/useOptionsGlobal';

import { API } from '@/utils/constants/Apis';
import { arrayToDict } from '@/utils/helpers/globalHelper';

interface IDropdownProps extends ISelectCustomProps {
  name: string;
  endPoint?: string;
  keyLabel?: string;
  keyValue?: string;
  multiSelect?: boolean;
  isCountryDropdown?: boolean;
}

const Dropdown: React.FC<IDropdownProps> = ({
  readOnly,
  multiSelect = false,
  placeholder,
  name,
  endPoint,
  keyLabel,
  keyValue,
  options,
  onChange,
  isCountryDropdown,
  ...props
}) => {
  const form = useFormContext();
  const nameOrigin = name.replace('answeredEn', 'answered');
  const masterEndpoint = (options?.length ? '' : endPoint) ?? '';
  const { data, loading } = useFetch(masterEndpoint ?? '', 'GET');
  const needGetCountryFlag =
    (endPoint && (API.GET_MASTER_COUNTRIES.includes(endPoint) || API.GET_MASTER_COUNTRIES_PUBLIC.includes(endPoint))) || isCountryDropdown;

  const dataOptions: DefaultOptionType[] = options?.length
    ? options ?? []
    : !needGetCountryFlag
      ? ((data as any[]) ?? []).map((i: any) => {
          return {
            label: i[keyLabel || ''],
            value: i[keyValue || 'id']
          };
        })
      : ((data as any[]) ?? []).map((i: any) => {
          return {
            label: i['countryName'],
            value: i['id']
          };
        });

  const countryDict = needGetCountryFlag ? arrayToDict(mappingOptionsCountry(dataOptions as any[]), 'value') : {};
  const optionRender = !needGetCountryFlag
    ? undefined
    : (option: DefaultOptionType) => {
        const country = countryDict[option?.value ?? ''];
        return (
          <>
            <div className='flex items-center base-input-select-option'>
              <AppTooltip title={option.label}>
                {country?.imgUrl && (
                  <div className='flex items-center'>
                    {!readOnly ? <country.FlagIcon className='mr-[11px]' /> : <CountryFlag countryCode={country?.imgUrl} />}
                    <span className='w-full'>{option.label}</span>
                  </div>
                )}
                {!country?.imgUrl && <span className='w-full'>{option.label}</span>}
              </AppTooltip>
            </div>
          </>
        );
      };

  useEffect(() => {
    const questionName = (nameOrigin ?? '').replace(/.answer.answered(\.[\d]+)*$/, '').replace(/.answer.answeredEn(\.[\d]+)*$/, '');
    if (endPoint && !options?.length && dataOptions.length) {
      form.setValue(
        `${questionName}.options`,
        dataOptions.map((op) => ({
          id: op.value,
          title: op.label
        }))
      );
    }
  }, [data]);

  useEffect(() => {
    if (multiSelect || !dataOptions.length) {
      return;
    }

    const answer = form.getValues(nameOrigin);
    if (answer?.id == null && answer?.title == null) {
      return;
    }

    const matchingOption = dataOptions.find(
      (option) =>
        (answer?.id != null && String(option.value) === String(answer.id)) || (answer?.title != null && String(option.label) === String(answer.title))
    );
    if (matchingOption && matchingOption.value !== answer?.id) {
      form.setValue(`${nameOrigin}.id`, matchingOption.value, {
        shouldDirty: false,
        shouldValidate: false,
        shouldTouch: false
      });
    }
  }, [data, options, multiSelect, nameOrigin]);

  return (
    <FormItem name={multiSelect ? `${nameOrigin}` : `${nameOrigin}.id`}>
      <SelectCustom
        id={nameOrigin}
        readOnly={readOnly}
        placeholder={readOnly ? '' : placeholder}
        loading={loading}
        options={masterEndpoint ? dataOptions : options}
        optionRender={optionRender}
        className='w-full h-full'
        allowClear={!readOnly}
        showSearch={!readOnly}
        onChange={(value) => {
          if (!multiSelect) {
            const option = dataOptions.find((i) => i.value === value);
            form.setValue(`${nameOrigin}.title`, option?.label ?? null, {
              shouldValidate: true,
              shouldDirty: true
            });
            option && onChange && onChange(value, option);
            return;
          }
        }}
        allowValueNotInOptions={false}
        mode={multiSelect ? 'multiple' : undefined}
        tagRender={
          !multiSelect
            ? undefined
            : (props) => {
                return (
                  <TagRenderComponent
                    label={props.label}
                    closable={true}
                    showCloseItem={false}
                    tagShowTooltip={false}
                    rest={undefined}
                    tagMouseDown={() => {}}
                    tagClosing={(_e, props) => {
                      form.setValue(
                        name,
                        form.getValues(name).filter((i: any) => i !== props.value),
                        { shouldDirty: true }
                      );
                    }}
                    props={props}
                  />
                );
              }
        }
        {...props}
      />
    </FormItem>
  );
};

export default Dropdown;
